import { useMemo } from 'react';
import { useGetProjectRespondentLookup } from '@/reactQuery/get';
import type { ResearchArtifact } from '@/reactQuery/researchAssistant';

/**
 * Shared respondent → conversation lookup type. Indexed by
 * `respondent_index` (the number a researcher sees in `[R12]`),
 * mapped to the conversation_id needed to open the transcript panel.
 *
 * Lives in its own file (not co-located with `CitationPillText`) so
 * the React-refresh linter doesn't complain about the component file
 * also exporting a hook + type. Fast Refresh wants components-only
 * exports per file.
 */
export type RespondentMap = Map<
  number,
  { conversationId: string; messageId?: string; questionId?: string }
>;

type ArtifactQuote = {
  respondent_index?: number | string;
  conversation_id?: string;
  message_id?: string;
  question_id?: string;
};

/**
 * Build a respondent → conversation lookup for the Research Assistant
 * citation pills (`[R12]` etc.).
 *
 * Two data sources, layered:
 *
 *   1. **Project-level lookup** (base layer) — `useGetProjectRespondentLookup`
 *      returns every (respondent_index → conversation_id) pair for the
 *      project. Fetched once and cached, so every respondent that
 *      exists in the project is clickable, regardless of whether any
 *      artifact in the current thread happens to mention them. Without
 *      this, theme_summary / markdown_report artifacts that cite
 *      respondents who aren't in the thread's quote_collection
 *      rendered disabled grey pills — which was the original UX bug.
 *
 *   2. **Artifact-scanned data** (overlay layer) — when a
 *      quote_collection artifact in the thread carries richer
 *      metadata for a respondent (specifically `message_id` and
 *      `question_id`), we prefer that entry so the transcript panel
 *      can scroll to the specific message instead of just opening at
 *      the top.
 *
 * Pass `projectId` to enable the project lookup. Pass `artifacts` to
 * enable the overlay. Either can be omitted (e.g. tests, isolated
 * embeds) and the hook degrades gracefully.
 */
export function useRespondentMap(
  artifacts: ResearchArtifact[] | undefined,
  projectId?: string,
): RespondentMap {
  const { data: projectLookup } = useGetProjectRespondentLookup(projectId);

  return useMemo(() => {
    const map: RespondentMap = new Map();

    // Base layer — project-wide respondent → conversation pairs.
    (projectLookup ?? []).forEach((entry) => {
      if (typeof entry.respondent_index === 'number' && typeof entry.conversation_id === 'string') {
        map.set(entry.respondent_index, {
          conversationId: entry.conversation_id,
        });
      }
    });

    // Overlay layer — artifact quotes may carry richer scroll-target
    // metadata. Merge on top of the base entry so a respondent who is
    // also cited in a quote_collection gets the precise message + question.
    (artifacts ?? [])
      .filter((artifact) => artifact.type === 'quote_collection')
      .forEach((artifact) => {
        const quotes: ArtifactQuote[] = artifact.data?.quotes || [];
        quotes.forEach((q) => {
          if (q.respondent_index == null || !q.conversation_id) return;
          const idx = Number(q.respondent_index);
          map.set(idx, {
            conversationId: q.conversation_id,
            messageId: q.message_id,
            questionId: q.question_id,
          });
        });
      });

    return map;
  }, [artifacts, projectLookup]);
}
